import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { spacing, fontWeights, borderRadius } from '../theme';
import { RIDERS } from '../data/riders';

export interface M07ProductOverviewProps {
  sumAssured: string;
  term: string;
  basePremium: number;
  selectedRiders: string[];
  r5Mode: 'complimentary' | 'attached';
  totalPremiumNum: number;
  r1Package?: 'silver' | 'gold' | 'platinum';
  r2LimitCuoi?: number;
  r3DailyAllowance?: number;
  r4PhysicalLimit?: number;
  r5SumAssured?: number;
  r5Term?: '20_years' | 'whole_life';
  riderPremiumOverrides?: Record<string, number>;
}

const formatNumber = (num: number): string => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const RIDERS_DATA = RIDERS.map((r) => ({
  id: r.id,
  name: r.name,
  price: r.basePrice,
  desc: r.shortDesc,
  color: r.color,
  category: r.category,
}));

export const M07ProductOverview: React.FC<M07ProductOverviewProps> = ({
  sumAssured,
  term,
  basePremium,
  selectedRiders,
  r5Mode,
  totalPremiumNum,
  r1Package = 'silver',
  r2LimitCuoi = 100000000,
  r3DailyAllowance = 1000000,
  r4PhysicalLimit = 100000000,
  r5SumAssured = 750000000,
  r5Term = '20_years',
  riderPremiumOverrides,
}) => {
  return (
    <View style={styles.subTabContent}>
      <View style={styles.responsiveTableWrapper}>
        <View style={styles.detailTable}>
          <View style={styles.detailTableHeader}>
            <Text style={[styles.detailTableCell, styles.detailTableHeaderCell, { width: 150 }]}>Sản phẩm tham gia</Text>
            <Text style={[styles.detailTableCell, styles.detailTableHeaderCell]}>Số tiền BH</Text>
            <Text style={[styles.detailTableCell, styles.detailTableHeaderCell]}>Thời hạn</Text>
            <Text style={[styles.detailTableCell, styles.detailTableHeaderCell, { width: 100 }]}>Phí đóng</Text>
          </View>
          <View style={styles.detailTableRow}>
            <Text style={[styles.detailTableCell, { width: 150, fontWeight: 'bold' }]}>Xanh Ước Mơ (Chính)</Text>
            <Text style={styles.detailTableCell}>{sumAssured} đ</Text>
            <Text style={styles.detailTableCell}>{term.replace(/[^0-9]/g, '')} năm</Text>
            <Text style={[styles.detailTableCell, { width: 100 }]}>{formatNumber(basePremium)} đ</Text>
          </View>
          {selectedRiders.map((id) => {
            const rider = RIDERS_DATA.find((r) => r.id === id);
            if (!rider) return null;

            // Determine dynamic sum assured (Số tiền BH)
            let riderSA = '';
            if (id === 'R1') {
              riderSA = r1Package === 'silver' ? '125.000.000 đ' : r1Package === 'gold' ? '250.000.000 đ' : '500.000.000 đ';
            } else if (id === 'R2') {
              riderSA = `${formatNumber(r2LimitCuoi)} đ`;
            } else if (id === 'R3') {
              riderSA = `${formatNumber(r3DailyAllowance)} đ/ngày`;
            } else if (id === 'R4') {
              riderSA = `${formatNumber(r4PhysicalLimit)} đ`;
            } else if (id === 'R5') {
              riderSA = r5Mode === 'attached' ? '950.000.000 đ' : '750.000.000 đ';
            } else {
              riderSA = '125.000.000 đ';
            }

            // Determine dynamic term
            let riderTerm = 'Tái tục hàng năm';
            if (id === 'R3' || id === 'R4') {
              riderTerm = 'Đến 75 tuổi';
            } else if (id === 'R5') {
              riderTerm = 'Đến 99 tuổi';
            }

            // Determine dynamic premium from overrides or fallback
            const overridePrice = riderPremiumOverrides?.[id];
            const finalPrice = overridePrice !== undefined ? overridePrice : rider.price;

            return (
              <View key={id} style={styles.detailTableRow}>
                <Text style={[styles.detailTableCell, { width: 150, color: rider.color }]}>{rider.name} (Rider)</Text>
                <Text style={styles.detailTableCell}>{riderSA}</Text>
                <Text style={styles.detailTableCell}>{riderTerm}</Text>
                <Text style={[styles.detailTableCell, { width: 100 }]}>
                  {finalPrice === 0 ? 'Tặng kèm' : `${formatNumber(finalPrice)} đ`}
                </Text>
              </View>
            );
          })}
          <View style={[styles.detailTableRow, { backgroundColor: '#f0fdf4' }]}>
            <Text style={[styles.detailTableCell, { width: 150, fontWeight: 'bold' }]}>Tổng tất cả</Text>
            <Text style={styles.detailTableCell}>--</Text>
            <Text style={styles.detailTableCell}>--</Text>
            <Text style={[styles.detailTableCell, { width: 100, color: '#00A651', fontWeight: 'bold' }]}>
              {formatNumber(totalPremiumNum)} đ
            </Text>
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  subTabContent: {
    gap: spacing[2],
  },
  responsiveTableWrapper: {
    width: '100%',
    overflowX: 'auto' as any,
  },
  detailTable: {
    width: '100%',
    minWidth: 380,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: borderRadius.sm,
    overflow: 'hidden',
  },
  detailTableHeader: {
    flexDirection: 'row',
    backgroundColor: '#00A651',
  },
  detailTableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
    backgroundColor: '#ffffff',
  },
  detailTableCell: {
    flex: 1,
    paddingVertical: spacing[2],
    paddingHorizontal: spacing[2],
    fontSize: 11,
    color: '#334155',
    textAlign: 'right',
  },
  detailTableHeaderCell: {
    color: '#ffffff',
    fontWeight: fontWeights.bold,
    textAlign: 'right',
  },
});
